import { Controller } from "@hotwired/stimulus"
import { closeModal, openModal } from "../../website/modal"

export default class extends Controller {
  static targets = ["quantity", "submit", "variant"]
  static values = { addUrl: String, errorMessage: String, stockUrl: String }

  connect() {
    this.element.dataset.quickShopReady = "true"
  }

  disconnect() {
    this.stockRequest?.abort()
    this.cartRequest?.abort()
    delete this.element.dataset.quickShopReady
  }

  async selectVariant(event) {
    const variant = event.currentTarget
    const label = variant.closest(".form-radio-label")
    this.stockRequest?.abort()
    const request = new AbortController()
    this.stockRequest = request
    this.setLoading(true)

    try {
      const url = new URL(this.stockUrlValue, window.location.origin)
      url.searchParams.set("variant_id", variant.value)
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Variant update failed with status ${response.status}`)

      const data = await response.json()
      const stock = label?.querySelector(".stock")
      if (stock) stock.textContent = data.stock_description || ""
      this.updateQuantity(data.current_stock)

      if (Number(data.current_stock) <= 0) {
        label?.classList.add("disabled")
        variant.disabled = true
        this.variantTargets.find(option => option.type === "radio" && !option.disabled)?.click()
      }
    } catch (error) {
      if (error.name !== "AbortError") this.showError()
    } finally {
      if (this.stockRequest === request) {
        this.stockRequest = null
        this.setLoading(false)
      }
    }
  }

  async addToCart(event) {
    event.preventDefault()
    const variantId = this.selectedVariantId()
    if (!variantId) return

    this.cartRequest?.abort()
    const request = new AbortController()
    this.cartRequest = request
    this.setLoading(true)

    try {
      const response = await fetch(this.addUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new URLSearchParams({ quantity: this.quantityTarget.value, variant_id: variantId }),
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Unable to add product (${response.status})`)

      this.handleCartResponse(await response.json(), variantId)
    } catch (error) {
      if (error.name !== "AbortError") this.showError()
    } finally {
      if (this.cartRequest === request) {
        this.cartRequest = null
        this.setLoading(false)
      }
    }
  }

  handleCartResponse(data, variantId) {
    if (data.type === "multi_sale") {
      const modal = document.querySelector("#modal-multi-sale")
      if (!modal) return

      modal.querySelector("#rebuild_variant_id").value = variantId
      modal.querySelector("#rebuild_quantity").value = this.quantityTarget.value
      openModal("modal-multi-sale")
      return
    }

    if (data.message) {
      this.showError(data.message)
      return
    }

    document.querySelectorAll("#header-cart").forEach(headerCart => {
      headerCart.innerHTML = data.cart
      headerCart.querySelectorAll("script").forEach(script => script.remove())
      headerCart.classList.add("open")
      window.setTimeout(() => headerCart.classList.remove("open"), 5000)
    })
    globalThis.countdown?.(data.cart_remaining_seconds)
    closeModal("modal")
  }

  selectedVariantId() {
    return this.variantTargets.find(variant => variant.checked)?.value ||
      this.variantTargets.find(variant => variant.type === "hidden")?.value
  }

  updateQuantity(currentStock) {
    const maximum = Math.min(Math.max(Number(currentStock) || 0, 0), 12)
    const options = Array.from({ length: maximum }, (_, index) => {
      const option = document.createElement("option")
      option.value = index + 1
      option.textContent = (index + 1).toString().padStart(2, "0")
      return option
    })
    if (options.length === 0) options.push(new Option("0", "0"))
    this.quantityTarget.replaceChildren(...options)
  }

  setLoading(loading) {
    this.element.toggleAttribute("aria-busy", loading)
    if (this.hasSubmitTarget) this.submitTarget.disabled = loading
  }

  showError(message) {
    const modal = document.querySelector("#modal")
    if (!modal) return

    openModal("modal")
    const title = modal.querySelector(".modal-header .title")
    const body = modal.querySelector(".modal-body")
    if (title) title.textContent = ""
    if (body) body.textContent = message || this.errorMessageValue
  }

}
